import { useEffect, useRef, useState } from 'react';

import { Copy01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/FormComponents';
import { SessionTimeoutManager } from 'components/SessionTimeoutManager/SessionTimeoutManager';

import { messagePopOut, selectApp } from 'api/app-slice';
import { selectAuthentication } from 'api/authentication-slice';

import { Loading } from '../../components/Loading/Loading';
import AppMessage from '../AppPage/AppMessage/AppMessage';
import { AdminNavbar } from './AdminNavbar/AdminNavbar';
import styles from './AdminPage.module.scss';

const AdminPage = ({
  title,
  subtitle,
  loading,
  button,
  buttonText = 'Save',
  children,
  sidebar = null,
  copyToClipboard = false,
  onClick = () => {},
  allowedRole,
}) => {
  const dispatch = useDispatch();
  const titleRef = useRef();

  const authentication = useSelector(selectAuthentication);
  const app = useSelector(selectApp);

  const [authenticating, setAuthenticating] = useState(true);
  const [copied, setCopied] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(messagePopOut());
  }, []);

  useEffect(() => {
    if (!authentication?.checked) return;
    else if (!authentication?.logged) navigate(`/sign_in?redirect_uri=${window.location.pathname}`);
    else if (!authentication?.admin) navigate('/dashboard');
    else if (!authentication.admin_permissions.includes(allowedRole)) navigate('/admin/companies');

    setAuthenticating(false);
  }, [authentication.checked]);

  const handleCopyToClipboard = async () => {
    if (titleRef.current) {
      try {
        await navigator.clipboard.writeText(titleRef.current.textContent);
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 5000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  };

  return (
    <div className={styles.root}>
      <Loading active={loading || authenticating}></Loading>
      <AdminNavbar />
      <AppMessage {...app.message} />
      {authentication?.logged && <SessionTimeoutManager />}
      <div className="flex-shrink-0 z-40 flex justify-between items-center bg-white py-8 px-16 border-b border-neutral-4">
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-2">
            <h1 ref={titleRef} className="text-4xl font-semibold text-neutral-7">
              {title}
            </h1>
            {subtitle && <p className="text-xl text-text-tertiary">{subtitle}</p>}
          </div>
          {copyToClipboard && (
            <OverlayTrigger
              key={title}
              overlay={<Tooltip>{copied ? 'Copied to clipboard' : 'Copy to clipboard'}</Tooltip>}>
              <HugeiconsIcon
                size={24}
                icon={Copy01Icon}
                className="u-pointer tertiary-text"
                strokeWidth={2}
                onClick={handleCopyToClipboard}
              />
            </OverlayTrigger>
          )}
        </div>
        {button && (
          <Button onClick={onClick} small>
            {buttonText}
          </Button>
        )}
      </div>
      <div className={styles.body}>
        {sidebar}
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};

export default AdminPage;
